import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Card, Badge, Form, Row, Col, Button, Spinner, Alert, Modal, Table } from 'react-bootstrap';
// Form used in SendDetailModal, Row/Col/Table used in modal stats/events
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import DatePeriodNavigation, { PeriodChangeEvent } from '../components/common/DatePeriodNavigation';
import {
  getMarketingEmailSends,
  useGetMarketingEmailLists,
  getMarketingEmailSendsIdEvents,
  useGetUsersId,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import type {
  MarketingEmailSend,
  MarketingEmailSendEvent,
  GetMarketingEmailSendsParamsStatus,
} from '../api/generated/model';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import { DataTable } from '../components/common/DataTable';
import type { ColumnConfig } from '../components/common/DataTable/types';
import EmailSendsFilterBar, { EmailSendsFiltersState } from '../components/EmailSendsFilterBar';

// Helper functions
function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
}

function formatPercent(value: number | undefined, total: number | undefined): string {
  if (!total || total === 0) return '0%';
  const v = value ?? 0;
  return `${((v / total) * 100).toFixed(1)}%`;
}

function getStatusBadgeVariant(status: string): string {
  switch (status) {
    case 'sent': return 'success';
    case 'sending': return 'primary';
    case 'scheduled': return 'info';
    case 'failed': return 'danger';
    default: return 'secondary';
  }
}

function getEventTypeBadgeVariant(eventType: string): string {
  switch (eventType) {
    case 'delivered': return 'success';
    case 'open':
    case 'opened': return 'info';
    case 'click':
    case 'clicked': return 'primary';
    case 'bounce':
    case 'bounced': return 'danger';
    case 'dropped':
    case 'blocked': return 'danger';
    case 'spamreport':
    case 'spam_report': return 'warning';
    case 'unsubscribe':
    case 'unsubscribed': return 'warning';
    case 'deferred': return 'secondary';
    case 'processed': return 'light';
    default: return 'secondary';
  }
}

// ========== Send Detail Modal ==========

const SendDetailModal: React.FC<{
  send: MarketingEmailSend | null;
  onHide: () => void;
}> = ({ send, onHide }) => {
  const [eventFilter, setEventFilter] = useState<string>('all');
  const [events, setEvents] = useState<MarketingEmailSendEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsLoaded, setEventsLoaded] = useState(false);

  // Load events when modal opens
  React.useEffect(() => {
    if (!send || eventsLoaded) return;
    setEventsLoading(true);
    getMarketingEmailSendsIdEvents(send.id, { sort_field: 'timestamp', sort_direction: 'desc' })
      .then(response => {
        setEvents(response.data ?? []);
        setEventsLoaded(true);
      })
      .catch(err => console.error('Failed to load events:', err))
      .finally(() => setEventsLoading(false));
  }, [send, eventsLoaded]);

  // Reset when send changes
  React.useEffect(() => {
    setEvents([]);
    setEventsLoaded(false);
    setEventFilter('all');
  }, [send?.id]);

  const filteredEvents = useMemo(() => {
    if (eventFilter === 'all') return events;
    const filterMap: Record<string, string[]> = {
      'processed': ['processed'],
      'delivered': ['delivered'],
      'opened': ['open', 'opened'],
      'clicked': ['click', 'clicked'],
      'bounced': ['bounce', 'bounced'],
      'deferred': ['deferred'],
      'dropped': ['dropped', 'blocked'],
      'spam_report': ['spamreport', 'spam_report'],
      'unsubscribed': ['unsubscribe', 'unsubscribed'],
    };
    const types = filterMap[eventFilter] ?? [eventFilter];
    return events.filter(e => types.includes(e.event_type));
  }, [events, eventFilter]);

  if (!send) return null;

  const totalRecipients = send.total_recipients ?? 0;
  const stats = send.stats;

  return (
    <Modal show={!!send} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          {send.sendgrid_singlesend_name || send.id}
          <Badge bg={getStatusBadgeVariant(send.status)} className="ms-2">
            {send.status.charAt(0).toUpperCase() + send.status.slice(1)}
          </Badge>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Metadata */}
        <div className="d-flex gap-4 mb-3 text-muted small">
          <span><strong>Sender:</strong> {send.sender_email || '-'}</span>
          <span><strong>Sent:</strong> {formatDate(send.sent_at || send.created_at)}</span>
          <span><strong>Recipients:</strong> {totalRecipients}</span>
        </div>

        {/* Stats */}
        <Row className="g-2 mb-4">
          {[
            { label: 'Processed', value: stats?.processed, total: totalRecipients },
            { label: 'Delivered', value: stats?.delivered, total: totalRecipients, variant: 'success' },
            { label: 'Opened', value: stats?.opened, total: stats?.delivered, variant: 'info' },
            { label: 'Clicked', value: stats?.clicked, total: stats?.delivered, variant: 'primary' },
            { label: 'Bounced', value: stats?.bounced, total: totalRecipients, variant: 'danger' },
            { label: 'Deferred', value: stats?.deferred, total: totalRecipients },
            { label: 'Dropped', value: stats?.dropped, total: totalRecipients, variant: 'danger' },
            { label: 'Spam', value: stats?.spam_reports, total: totalRecipients, variant: 'warning' },
            { label: 'Unsub', value: stats?.unsubscribed, total: totalRecipients, variant: 'warning' },
          ].map(s => (
            <Col key={s.label} xs={6} md={4} lg>
              <div className="text-center p-2 border rounded">
                <div className={`h5 mb-0 text-${s.variant || 'secondary'}`}>{s.value ?? 0}</div>
                <div className="small text-muted">{s.label}</div>
                <div className="small text-muted">{formatPercent(s.value, s.total)}</div>
              </div>
            </Col>
          ))}
        </Row>

        {/* Lists */}
        {send.lists && send.lists.length > 0 && (
          <div className="mb-3">
            <strong className="small">Recipient Lists:</strong>{' '}
            {send.lists.map((list, idx) => (
              <Badge key={list.id || idx} bg="secondary" className="me-1">
                {list.name || list.id}
              </Badge>
            ))}
          </div>
        )}

        {/* Events */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0">Events ({filteredEvents.length})</h6>
          <Form.Select
            size="sm"
            style={{ width: 'auto' }}
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value)}
          >
            <option value="all">All Events</option>
            <option value="processed">Processed</option>
            <option value="delivered">Delivered</option>
            <option value="opened">Opened</option>
            <option value="clicked">Clicked</option>
            <option value="bounced">Bounced</option>
            <option value="deferred">Deferred</option>
            <option value="dropped">Dropped</option>
            <option value="spam_report">Spam Reports</option>
            <option value="unsubscribed">Unsubscribed</option>
          </Form.Select>
        </div>
        {eventsLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
            <span className="ms-2">Loading events...</span>
          </div>
        ) : (
          <Card style={{ overflow: 'hidden' }}>
            <Card.Body className="p-0" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <Table size="sm" striped hover className="mb-0">
                <thead style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: 'var(--bs-body-bg)' }}>
                  <tr>
                    <th>Email</th>
                    <th>Event Type</th>
                    <th>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEvents.map(event => (
                    <tr key={event.id}>
                      <td>{event.email}</td>
                      <td>
                        <Badge bg={getEventTypeBadgeVariant(event.event_type)}>
                          {event.event_type}
                        </Badge>
                      </td>
                      <td>{formatDate(event.timestamp)}</td>
                    </tr>
                  ))}
                  {filteredEvents.length === 0 && (
                    <tr>
                      <td colSpan={3} className="text-center text-muted py-3">
                        No events found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}
      </Modal.Body>
    </Modal>
  );
};

// ========== Main Component ==========

const EmailSendsTab: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { data: userData } = useGetUsersId(user?.id || '', { query: { enabled: !!user?.id } });
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize from URL params
  const [filters, setFilters] = useState<EmailSendsFiltersState>(() => {
    const initial: EmailSendsFiltersState = {};
    for (const key of ['search', 'status', 'list_id'] as const) {
      const val = searchParams.get(key);
      if (val) (initial as any)[key] = val;
    }
    return initial;
  });
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [sortField, setSortField] = useState(searchParams.get('sort_field') || 'created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>((searchParams.get('sort_dir') as 'asc' | 'desc') || 'desc');

  // Date range from DatePeriodNavigation
  const [dateRange, setDateRange] = useState<{ start_date: string; end_date: string }>({
    start_date: '', end_date: '',
  });

  const handlePeriodChange = (event: PeriodChangeEvent) => {
    setDateRange(event.dateRange);
  };

  // Detail modal
  const [selectedSend, setSelectedSend] = useState<MarketingEmailSend | null>(null);

  // Fetch lists for filter dropdown
  const { data: listsData } = useGetMarketingEmailLists();
  const lists = listsData ?? [];

  const FETCH_LIMIT = 50;

  // Sync state to URL params — only when this tab is active
  const isActiveTab = window.location.pathname.endsWith('/sends');
  useEffect(() => {
    if (!isActiveTab) return;
    const params = new URLSearchParams();
    params.set('sort_field', sortField);
    params.set('sort_dir', sortDir);
    if (dateRange.start_date) params.set('start_date', dateRange.start_date.slice(0, 10));
    if (dateRange.end_date) params.set('end_date', dateRange.end_date.slice(0, 10));
    for (const [key, val] of Object.entries(filters)) {
      if (val) params.set(key, val);
    }
    setSearchParams(params, { replace: true });
  }, [filters, dateRange, sortField, sortDir, isActiveTab, setSearchParams]);

  // Build query params for cursor pagination
  const buildQueryParams = useCallback(
    (cursor: string | undefined) =>
      cursor
        ? { cursor, limit: FETCH_LIMIT }
        : {
            sort_field: sortField,
            sort_direction: sortDir,
            search: filters.search || undefined,
            status: filters.status as GetMarketingEmailSendsParamsStatus || undefined,
            list_id: filters.list_id || undefined,
            start_date: dateRange.start_date || undefined,
            end_date: dateRange.end_date || undefined,
            limit: FETCH_LIMIT,
          },
    [filters, dateRange, sortField, sortDir]
  );

  // Row identity. Shared with the hook's duplicate-detection and DataTable.
  const getRowKey = useCallback((send: MarketingEmailSend) => send.id || '', []);

  const {
    allItems: sends,
    totalCount: total,
    isLoading,
    error,
    hasMore,
    handleLoadMore,
  } = usePaginatedQuery<MarketingEmailSend, Awaited<ReturnType<typeof getMarketingEmailSends>>>({
    queryKey: 'email_sends',
    buildQueryParams,
    queryFn: (params) => getMarketingEmailSends(params as any),
    getRowKey,
    enabled: !!dateRange.start_date && !!dateRange.end_date,
  });

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const handleFiltersChange = (newFilters: EmailSendsFiltersState) => {
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
  };

  const handleSortChange = useCallback((field: string, dir: 'asc' | 'desc') => {
    setSortField(field);
    setSortDir(dir);
  }, []);

  const columns: ColumnConfig<any>[] = useMemo(() => [
    {
      key: 'name', label: 'Send Name', width: 250, sortable: true,
      sortValue: (send: any) => send.sendgrid_singlesend_name || send.id,
      renderCell: (send: any) => send.sendgrid_singlesend_name || send.id,
    },
    {
      key: 'sender_email', label: 'Sender', width: 180, sortable: true,
      renderCell: (send: any) => send.sender_email || '-',
    },
    {
      key: 'created_at', label: 'Sent Date', width: 170, sortable: true,
      renderCell: (send: any) => formatDate(send.sent_at || send.created_at),
    },
    {
      key: 'total_recipients', label: 'Recipients', width: 100, sortable: true,
      renderCell: (send: any) => <span className="d-block text-end">{send.total_recipients ?? 0}</span>,
    },
    {
      key: 'delivered', label: 'Delivered', width: 100,
      renderCell: (send: any) => {
        const stats = send.stats;
        const total = send.total_recipients ?? 0;
        return (
          <span className="d-block text-end">
            <span className="text-success">{stats?.delivered ?? 0}</span>
            <small className="text-muted ms-1">{formatPercent(stats?.delivered, total)}</small>
          </span>
        );
      },
    },
    {
      key: 'opened', label: 'Opened', width: 100,
      renderCell: (send: any) => {
        const stats = send.stats;
        return (
          <span className="d-block text-end">
            <span className="text-info">{stats?.opened ?? 0}</span>
            <small className="text-muted ms-1">{formatPercent(stats?.opened, stats?.delivered)}</small>
          </span>
        );
      },
    },
    {
      key: 'clicked', label: 'Clicked', width: 100,
      renderCell: (send: any) => {
        const stats = send.stats;
        return (
          <span className="d-block text-end">
            <span className="text-primary">{stats?.clicked ?? 0}</span>
            <small className="text-muted ms-1">{formatPercent(stats?.clicked, stats?.delivered)}</small>
          </span>
        );
      },
    },
    {
      key: 'bounced', label: 'Bounced', width: 100,
      renderCell: (send: any) => {
        const stats = send.stats;
        const total = send.total_recipients ?? 0;
        return (
          <span className="d-block text-end">
            <span className="text-danger">{stats?.bounced ?? 0}</span>
            <small className="text-muted ms-1">{formatPercent(stats?.bounced, total)}</small>
          </span>
        );
      },
    },
    {
      key: 'status', label: 'Status', width: 100, sortable: true,
      renderCell: (send: any) => (
        <Badge bg={getStatusBadgeVariant(send.status)}>
          {send.status.charAt(0).toUpperCase() + send.status.slice(1)}
        </Badge>
      ),
    },
  ], []);

  return (
    <div className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
      <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: '240px', overflow: 'hidden' }}>
        <Card.Header className="bg-primary text-white flex-shrink-0">
          <div className="d-flex justify-content-between align-items-center m-1 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="bg-header-control rounded px-2 py-1 d-flex align-items-center gap-2">
                <span className="text-primary fw-semibold">
                  <i className="bi bi-send me-1"></i>
                  Sends for
                </span>
                <DatePeriodNavigation
                  onPeriodChange={handlePeriodChange}
                />
                <div className="border-end border-primary mx-2" style={{ height: '24px' }}></div>
                <Button
                  variant={activeFilterCount > 0 ? "primary" : "outline-primary"}
                  size="sm"
                  onClick={() => setShowFilterBar(!showFilterBar)}
                >
                  <i className="bi bi-funnel me-1"></i>
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="badge bg-light text-primary ms-1">{activeFilterCount}</span>
                  )}
                </Button>
              </div>
            </div>
            <div className="rounded px-3 py-1">
              <span className="text-white">
                {isLoading ? 'Loading...' : `${total ?? sends.length} total sends`}
              </span>
            </div>
          </div>
          {showFilterBar && (
            <EmailSendsFilterBar
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onClearFilters={clearFilters}
              listOptions={lists.map(list => ({ value: list.id || '', label: list.name || '' }))}
            />
          )}
        </Card.Header>

        <Card.Body className="flex-grow-1 overflow-hidden p-0" style={{ minHeight: 0 }}>
          <DataTable
            data={sends}
            columns={columns}
            getRowKey={getRowKey}
            onRowClick={(send: any) => setSelectedSend(send)}
            sortConfig={{ field: sortField, direction: sortDir }}
            onSortChange={handleSortChange}
            isLoading={isLoading}
            pagination={hasMore ? { hasMore, cursor: '', limit: FETCH_LIMIT } : undefined}
            onLoadMore={handleLoadMore}
            enableColumnReorder={true}
            enableColumnResize={true}
            enableColumnVisibility={true}
            enableStickyHeader={true}
            preferencesKey="email_sends"
            userId={user?.id}
            userPreferences={userData?.preferences}
            maxHeight="100%"
            emptyState={
              error ? (
                <Alert variant="danger" className="m-3">Failed to load email sends.</Alert>
              ) : (
              <div className="text-center py-4 text-muted">
                <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                No sends found
              </div>
              )
            }
          />
        </Card.Body>
      </Card>

      <SendDetailModal send={selectedSend} onHide={() => setSelectedSend(null)} />
    </div>
  );
};

export default EmailSendsTab;
